import { useState, useMemo } from "react";
import { usePeriodicLoadData } from "../hooks/usePeriodicLoadData";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/common/Card";
import Spinner from "./common/Spinner";
import { supabase } from "../lib/supabase";
import { Badge } from "../components/common/Badge";
import { ConsultationModeBadge } from "./ConsultationMode";
import Button from "../components/common/Button";
import { useAuth } from "../lib/AuthContext";
import { useData } from "../lib/DataContext";
import { useNavigate } from "react-router-dom";
import * as utils from "../utils/utils";
import {
  Clock,
  User,
  Phone,
  FileText,
  CheckCircle,
  Users,
  Calendar,
  Stethoscope,
  ArrowLeft,
} from "lucide-react";
import customToast from "./common/Toaster";
import { formatClockTime, formatDateDMY } from "../lib/ScheduleUtils";
import { getLocalizedDayName } from "../i18n/scheduleLabels";
import useBrandTranslation from "../hooks/useBrandTranslation";

export default function WaitingRoomDoctor() {
  const { t } = useBrandTranslation();
  const { user } = useAuth();
  const { appointments, profiles, loadData } = useData();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [updatingAppointment, setUpdatingAppointment] = useState(null);

  usePeriodicLoadData(loadData, 10_000, user?.role === "doctor" && !!user?.id);

  // Filtrar turnos presentes del doctor actual
  const presentAppointments = useMemo(() => {
    if (!user || user.role !== "doctor" || !appointments) return [];

    const today = new Date().toISOString().split("T")[0];

    // Debug: mostrar todos los appointments del doctor de hoy
    const doctorAppointmentsToday = appointments.filter(
      (apt) => apt.doctor_id === user.id && apt.date === today
    );
    console.log("🔍 Appointments del doctor hoy:", {
      doctorId: user.id,
      today,
      total: doctorAppointmentsToday.length,
      appointments: doctorAppointmentsToday.map((apt) => ({
        id: apt.id,
        date: apt.date,
        status: apt.status,
        patient_name: apt.patient_name,
        start_time: apt.start_time,
        doctor_id: apt.doctor_id,
        doctor_id_match: apt.doctor_id === user.id,
        date_match: apt.date === today,
      })),
    });

    const filtered = appointments
      .filter(
        (apt) =>
          apt.doctor_id === user.id &&
          apt.status === "present" &&
          apt.date === today
      )
      .sort((a, b) => a.start_time.localeCompare(b.start_time));

    console.log("✅ Turnos filtrados (present):", filtered.length, filtered);

    return filtered;
  }, [appointments, user]);

  // Obtener información del paciente
  const getPatientInfo = (appointment) => {
    if (appointment.patient_name) {
      return {
        name: appointment.patient_name,
        phone: appointment.patient_phone || "No disponible",
        email: "No disponible",
      };
    }

    const patient = profiles.find((p) => p.id === appointment.patient_id);
    return {
      name: patient ? `${patient.name} ${patient.last_name}` : "Paciente",
      phone: patient?.phone || "No disponible",
      email: patient?.email || "No disponible",
    };
  };

  // Marcar turno como completado
  const markAsCompleted = async (appointmentId) => {
    setUpdatingAppointment(appointmentId);
    try {
      const { error } = await supabase
        .from("appointments")
        .update({ status: "completed" })
        .eq("id", appointmentId);

      if (error) throw error;

      // Recargar datos
      await loadData();
    } catch (error) {
      console.error("Error updating appointment:", error);
      customToast(t("app.appointments.updateError", "Error al actualizar el turno"), "danger");
    } finally {
      setUpdatingAppointment(null);
    }
  };

  // Ir a historia clínica
  const goToClinicalHistory = (appointment) => {
    console.log(appointment);
    if (appointment.patient_id) {
      // Ver historia clínica del paciente
      navigate(
        `/doctor/clinical-history/view?appointmentId=${appointment.id}&patientId=${appointment.patient_id}&doctorId=${appointment.doctor_id}`
      );
    } else {
      // Si no hay patient_id, no se puede crear historia clínica porque el paciente no está registrado
      // Mostrar mensaje informativo
      customToast(
        t(
          "app.appointments.unregisteredPatientHistory",
          "Este paciente no está registrado en el sistema. No se puede crear una historia clínica para pacientes no registrados."
        ),
        "warning"
      );
    }
  };

  if (!user) return <Spinner className="w-10 h-10 mx-auto" />;

  if (user && user.role !== "doctor") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>{t("app.appointments.restrictedAccess", "Acceso Restringido")}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600">{t("app.appointments.doctorsOnly", "Esta sección es solo para médicos.")}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <div className="flex items-center gap-4 mb-4">
              <Button
                variant="outline"
                onClick={() => utils.goBack()}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />{t("common.backHome", "Volver al Inicio")}</Button>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              <Users className="w-8 h-8 text-blue-600" />
              {t("app.waitingRoom.title", "Recepción de Pacientes")}
            </h1>
            <p className="text-gray-600 mt-2">
              {t(
                "app.waitingRoom.subtitleDoctor",
                "Pacientes presentes en recepción — Dr. {{name}} {{lastName}}",
                { name: user.name, lastName: user.last_name }
              )}
            </p>
            <p className="text-sm text-gray-500">
              {getLocalizedDayName(new Date().getDay())} {formatDateDMY(new Date())}
            </p>
          </div>
        </div>

        {/* Estadísticas */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card>
            <CardContent className="!pt-5">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-600" />
                <div className="flex items-center gap-2">
                  <p className="text-2xl font-bold">
                    {presentAppointments.length}
                  </p>
                  <p className="text-sm text-gray-600">{t("app.waitingRoom.patientsWaiting", "Pacientes Esperando")}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="!pt-5">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-orange-600" />
                <div className="flex items-center gap-2">
                  <p className="text-2xl font-bold">
                    {presentAppointments.length > 0
                      ? formatClockTime(presentAppointments[0].start_time)
                      : "--:--"}
                  </p>
                  <p className="text-sm text-gray-600">{t("app.waitingRoom.nextAppointment", "Próximo Turno")}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="!pt-5">
              <div className="flex items-center gap-2">
                <Stethoscope className="w-5 h-5 text-green-600" />
                <div className="flex items-center gap-2">
                  <p className="text-2xl font-bold">
                    {
                      presentAppointments.filter(
                        (apt) => apt.appointment_type === "private"
                      ).length
                    }
                  </p>
                  <p className="text-sm text-gray-600">{t("app.waitingRoom.private", "Particulares")}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Lista de Pacientes */}
        <Card className="mb-20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              {t("app.waitingRoom.patientsInReception", "Pacientes en recepción")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {presentAppointments.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg">
                  {t("app.waitingRoom.emptyNoPatients", "No hay pacientes en recepción")}
                </p>
                <p className="text-gray-400 text-sm">
                  {t(
                    "app.waitingRoom.emptyNoPatientsHint",
                    "Los pacientes aparecerán aquí cuando la secretaria los marque como presentes"
                  )}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {presentAppointments.map((appointment, index) => {
                  const patientInfo = getPatientInfo(appointment);
                  const isUpdating = updatingAppointment === appointment.id;

                  return (
                    <div
                      key={appointment.id}
                      className={`border rounded-lg p-3 sm:p-4 transition-all hover:shadow-md ${
                        index === 0 ? "border-blue-500 bg-blue-50" : ""
                      }`}
                    >
                      {/* Header: Hora y badges */}
                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 mb-3">
                        {/* Hora y nombre del paciente */}
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                          <div className="flex flex-col items-center flex-shrink-0">
                            <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500" />
                            <span className="text-xs sm:text-sm font-medium mt-1">
                              {formatClockTime(appointment.start_time)}
                            </span>
                            <span className="text-xs text-gray-400">
                              {formatClockTime(appointment.end_time)}
                            </span>
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2 mb-1">
                              <User className="w-4 h-4 text-gray-500 flex-shrink-0" />
                              <span className="font-medium text-base sm:text-lg truncate">
                                {patientInfo.name}
                              </span>
                              {index === 0 && (
                                <Badge variant="default" className="text-xs flex-shrink-0">
                                  Próximo
                                </Badge>
                              )}
                            </div>

                            <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-xs sm:text-sm text-gray-600">
                              {patientInfo.phone !== "No disponible" && (
                                <div className="flex items-center gap-1">
                                  <Phone className="w-3 h-3 flex-shrink-0" />
                                  <span className="truncate">{patientInfo.phone}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Badges y precio - se mueven abajo en móvil */}
                        <div className="flex flex-row sm:flex-col items-start sm:items-end gap-2 flex-shrink-0">
                          <Badge
                            variant={
                              appointment.appointment_type === "private"
                                ? "appointmentPrivate"
                                : "appointmentInsurance"
                            }
                            className="text-xs"
                          >
                            {appointment.appointment_type === "private"
                              ? "Particular"
                              : "Obra Social"}
                          </Badge>
                          <ConsultationModeBadge
                            mode={appointment.consultation_mode}
                            className="text-xs"
                          />

                          {appointment.price && (
                            <span className="text-xs sm:text-sm font-medium text-green-600 whitespace-nowrap">
                              ${appointment.price}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Motivo de consulta */}
                      {appointment.notes && (
                        <div className="mb-3 text-xs sm:text-sm text-gray-700 bg-gray-50 p-2 rounded">
                          <strong>{t("app.waitingRoom.reason", "Motivo:")}</strong> {appointment.notes}
                        </div>
                      )}

                      {/* Botones - apilados en móvil, en fila en desktop */}
                      <div className="flex flex-col sm:flex-row justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => goToClinicalHistory(appointment)}
                          className="w-full sm:w-auto"
                        >
                          <FileText className="w-4 h-4 mr-2" />
                          Historia Clínica
                        </Button>

                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => markAsCompleted(appointment.id)}
                          disabled={isUpdating}
                          className="w-full sm:w-auto"
                        >
                          <CheckCircle className="w-4 h-4 mr-2" />
                          {isUpdating ? "Completando..." : "Marcar Completado"}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
